/**
 * Typed bindings + React Query hooks for /api/team/* (company-scoped).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import type { Role } from "./auth";

export interface TeamMember {
  id: string;
  username: string;
  role: Role;
  is_active: boolean;
  is_activated: boolean;
  created_at: string;
  last_login_at: string | null;
}

export interface InviteCreateResponse {
  invite_url: string;
  invite_expires_at: string;
  user_id: string;
}

export const TEAM_QUERY_KEY = ["team"] as const;

export function useTeam() {
  return useQuery<TeamMember[]>({
    queryKey: TEAM_QUERY_KEY,
    queryFn: () => api.get<TeamMember[]>("/api/team"),
  });
}

export function useInviteTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { username: string; role: Role }) =>
      api.post<InviteCreateResponse>("/api/team/invites", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: TEAM_QUERY_KEY }),
  });
}

export function useUpdateTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; role?: Role; is_active?: boolean }) =>
      api.patch<TeamMember>(`/api/team/${vars.id}`, {
        role: vars.role,
        is_active: vars.is_active,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: TEAM_QUERY_KEY }),
  });
}

export interface ResetLinkResponse {
  reset_url: string;
  expires_at: string;
  username: string;
}

/** Issue a fresh single-use password link for a member who is locked out.
 * Revokes their earlier unused links server-side. */
export function useIssueResetLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.post<ResetLinkResponse>(`/api/team/${userId}/reset-link`),
    onSuccess: () => qc.invalidateQueries({ queryKey: TEAM_QUERY_KEY }),
  });
}

/** Detach the member's authenticator — next login enrols a new one. */
export function useResetMember2fa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.post<void>(`/api/team/${userId}/reset-2fa`),
    onSuccess: () => qc.invalidateQueries({ queryKey: TEAM_QUERY_KEY }),
  });
}

export function useRemoveTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/team/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: TEAM_QUERY_KEY }),
  });
}
